/**
 * Tips Desk helpers — unifies AI signals + community (crowd) tips into a single
 * fixture-centric, analytical board. One fixture row aggregates the AI read and
 * every crowd tip on that match, plus a consensus/edge layer computed on top.
 */

import {
  aiConfidence,
  canViewCommunity,
  confidenceTone,
  fixtureStatusTone,
  normalizePercent,
} from "../../../utils/predictionHelpers";
import {
  fixtureHasFavoriteTeam,
  isBetFromFollowedCreator,
} from "../../../utils/p2pHelpers";
import { formatAppDate, formatAppTime } from "../../../utils/appTimeFormat";
import { sortLeaguesByPriority } from "../../../utils/leaguePriority";

/** Closed tips stay visible this long after kickoff (aligned with backend tips feed). */
export const TIPS_CLOSED_RETENTION_HOURS = 12;

export const TIPS_FILTERS = [
  { id: "all", label: "All" },
  { id: "ai", label: "AI" },
  { id: "crowd", label: "Crowd" },
  { id: "following", label: "Following" },
  { id: "favorites", label: "Favorites" },
  { id: "live", label: "Live" },
  { id: "closed", label: "Closed" },
  { id: "today", label: "Today" },
  { id: "tomorrow", label: "Tomorrow" },
  { id: "unlocked", label: "Unlocked" },
];

export const TIPS_SORT_OPTIONS = [
  { id: "signal", label: "Strongest prediction" },
  { id: "kickoff", label: "Soonest kickoff" },
  { id: "crowd", label: "Most crowd tips" },
  { id: "agreement", label: "AI ↔ crowd agreement" },
];

const startOfDay = (d = new Date()) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const sameDayTs = (ts, offsetDays = 0) => {
  if (!ts) return false;
  const target = startOfDay();
  target.setDate(target.getDate() + offsetDays);
  const d = new Date(Number(ts) * 1000);
  return (
    d.getFullYear() === target.getFullYear() &&
    d.getMonth() === target.getMonth() &&
    d.getDate() === target.getDate()
  );
};

export const getAIFixtureTs = (item) =>
  item?.fixture?.timestamp || item?.fixtureData?.timestamp || null;

export const getCommunityFixtureTs = (item) => item?.fixture?.timestamp || null;

const aiFixtureId = (item) => {
  const f = item?.fixture || item?.fixtureData;
  const id = f?._id || f?.id || f?.fixtureId || item?.fixtureId;
  return id != null ? String(id) : null;
};

const communityFixtureId = (item) => {
  const f = item?.fixture;
  const id = f?._id || f?.id;
  return id != null ? String(id) : null;
};

const leagueOf = (fixture) => fixture?.league || {};

/** Highest-confidence outcome across one community prediction. */
const leadOutcome = (prediction) => {
  const outcomes = prediction?.outcomes || [];
  if (!outcomes.length) return null;
  return [...outcomes].sort(
    (a, b) => (parseInt(b.confidence, 10) || 0) - (parseInt(a.confidence, 10) || 0)
  )[0];
};

/** AI lean as a short pick label + tone, derived from winner / percent split. */
export const aiLean = (prediction) => {
  if (!prediction) return null;
  const pred = prediction.predictions || {};
  const percent = normalizePercent(pred.percent || prediction.percent || {});
  const homeName =
    prediction.homeTeam?.name || prediction.fixture?.teams?.home?.name || "Home";
  const awayName =
    prediction.awayTeam?.name || prediction.fixture?.teams?.away?.name || "Away";

  let pick = pred.winner?.name || null;
  if (!pick) {
    const max = Math.max(percent.home, percent.draw, percent.away);
    if (max <= 0) pick = null;
    else if (max === percent.home) pick = homeName;
    else if (max === percent.away) pick = awayName;
    else pick = "Draw";
  }
  return {
    pick,
    confidence: aiConfidence(prediction),
    percent,
  };
};

/**
 * Crowd consensus across community tips on a fixture.
 * When `userId` is passed, locked premium picks are excluded from the lean
 * (count still reflects every tipster on the match).
 */
export const crowdConsensus = (communityList = [], userId = null) => {
  if (!communityList.length) {
    return {
      count: 0,
      topPick: null,
      agreement: 0,
      avgConfidence: 0,
      readableCount: 0,
      distribution: [],
      lockedCount: 0,
    };
  }

  const readable =
    userId == null
      ? communityList
      : communityList.filter((p) => canViewCommunity(p, userId));
  const lockedCount = communityList.length - readable.length;

  const tally = new Map();
  let confSum = 0;
  let confCount = 0;
  readable.forEach((p) => {
    const lead = leadOutcome(p);
    if (!lead?.outcome) return;
    const key = lead.outcome;
    const conf = parseInt(lead.confidence, 10) || 0;
    const prev = tally.get(key) || { count: 0, conf: 0, tipsters: [] };
    prev.count += 1;
    prev.conf += conf;
    prev.tipsters.push({
      id: p._id,
      name: p.user?.name || "Tipster",
      confidence: conf,
      isPremium: Boolean(p.isPremium),
    });
    tally.set(key, prev);
    confSum += conf;
    confCount += 1;
  });

  let topPick = null;
  let topCount = 0;
  tally.forEach((val, key) => {
    if (val.count > topCount) {
      topCount = val.count;
      topPick = key;
    }
  });

  const readableWithPick = confCount;
  const distribution = Array.from(tally.entries())
    .map(([outcome, v]) => ({
      outcome,
      count: v.count,
      share: readableWithPick ? Math.round((v.count / readableWithPick) * 100) : 0,
      avgConfidence: Math.round(v.conf / v.count),
      tipsters: v.tipsters,
    }))
    .sort((a, b) => b.count - a.count || b.avgConfidence - a.avgConfidence);

  return {
    count: communityList.length,
    readableCount: readable.length,
    lockedCount,
    topPick,
    agreement: readableWithPick ? Math.round((topCount / readableWithPick) * 100) : 0,
    avgConfidence: confCount ? Math.round(confSum / confCount) : 0,
    distribution,
  };
};

/** Loosely normalize a pick label so AI vs crowd picks can be compared. */
export const normalizePick = (label) =>
  String(label || "")
    .toLowerCase()
    .replace(/\bto win\b/g, "")
    .replace(/[^a-z0-9 ]/g, "")
    .trim();

export const picksAlign = (a, b) => {
  const left = normalizePick(a);
  const right = normalizePick(b);
  if (!left || !right) return false;
  return left === right || left.includes(right) || right.includes(left);
};

/** Do AI and crowd point the same way? Returns 'agree' | 'split' | null. */
export const aiCrowdEdge = (ai, consensus) => {
  if (!ai?.pick || !consensus?.topPick) return null;
  return picksAlign(ai.pick, consensus.topPick) ? "agree" : "split";
};

/**
 * Side-by-side outcome agreement: tipster distribution + whether AI lands on each row.
 */
export const buildOutcomeAgreement = (entry, userId = null) => {
  const consensus = entry?.consensus?.distribution
    ? entry.consensus
    : crowdConsensus(entry?.community || [], userId);
  const ai = entry?.aiReadout || aiLean(entry?.ai);
  const aiLocked = Boolean(entry?.ai && !entry.ai.isUnlocked);
  const aiPick = !entry?.ai || aiLocked ? null : ai?.pick || null;
  const aiConfidence = ai?.confidence || 0;

  const rows = (consensus.distribution || []).map((row) => ({
    ...row,
    aiMatch: aiPick ? picksAlign(aiPick, row.outcome) : false,
  }));

  // If AI has a pick that no readable tipster shares, append it as an AI-only row.
  if (aiPick && !rows.some((r) => r.aiMatch)) {
    rows.push({
      outcome: aiPick,
      count: 0,
      share: 0,
      avgConfidence: aiConfidence,
      tipsters: [],
      aiMatch: true,
      aiOnly: true,
    });
  }

  const alignedRow = rows.find((r) => r.aiMatch && r.count > 0) || null;
  const tipsterAgreement = consensus.agreement || 0;
  const aiCrowd =
    aiPick && consensus.topPick
      ? picksAlign(aiPick, consensus.topPick)
        ? "agree"
        : "split"
      : aiPick && !consensus.topPick
        ? "ai-only"
        : !aiPick && consensus.topPick
          ? "crowd-only"
          : null;

  return {
    rows,
    tipsterCount: consensus.count || 0,
    readableCount: consensus.readableCount || 0,
    lockedCount: consensus.lockedCount || 0,
    tipsterAgreement,
    topCrowdPick: consensus.topPick || null,
    aiPick,
    aiConfidence,
    aiLocked: Boolean(entry?.ai && aiLocked),
    hasAI: Boolean(entry?.ai),
    aiCrowd,
    alignedShare: alignedRow?.share || 0,
    alignedCount: alignedRow?.count || 0,
  };
};

/** Composite 0–100 "signal strength" used for the default board sort. */
export const signalStrength = (entry) => {
  const aiPart = entry.ai ? aiLean(entry.ai)?.confidence || 0 : 0;
  const crowd = entry.consensus;
  const crowdPart = crowd.count
    ? Math.min(100, crowd.avgConfidence * 0.6 + crowd.count * 8)
    : 0;
  const edgeBonus = entry.edge === "agree" ? 12 : 0;
  return Math.round(Math.max(aiPart, crowdPart) + edgeBonus);
};

export const tipsFixtureStatus = (fixture) => fixtureStatusTone(fixture);

export const tipsConfidenceTone = (value) => confidenceTone(value);

const statusSortRank = (entry) => {
  const tone = entry?.status?.tone;
  if (tone === "live") return 0;
  if (tone === "upcoming") return 1;
  return 2; // finished / closed last
};

/** Drop closed fixtures once the tips retention window has elapsed. */
export const isWithinTipsRetention = (entry, nowMs = Date.now()) => {
  if (entry?.status?.tone !== "finished") return true;
  const ts = Number(entry?.ts || entry?.fixture?.timestamp || 0);
  if (!Number.isFinite(ts) || ts <= 0) return false;
  const floorMs = nowMs - TIPS_CLOSED_RETENTION_HOURS * 60 * 60 * 1000;
  return ts * 1000 >= floorMs;
};

/** Visible-to-this-user community tips (premium gating respected). */
export const visibleCrowd = (communityList, userId) =>
  (communityList || []).filter((p) => canViewCommunity(p, userId));

export const formatKickoffMeta = (ts) => {
  if (!ts) return { time: "TBD", date: "Kickoff" };
  const d = new Date(Number(ts) * 1000);
  return { time: formatAppTime(d), date: formatAppDate(d) };
};

/**
 * Builds the league → fixture board. Each fixture entry carries the AI signal
 * (if any), the list of community tips, and a computed consensus/edge layer.
 */
export const buildTipsBoard = (community = [], ai = [], userId = null) => {
  const fixtureMap = new Map();

  const ensure = (fixtureId, fixture) => {
    if (!fixtureId) return null;
    if (!fixtureMap.has(fixtureId)) {
      fixtureMap.set(fixtureId, {
        fixtureId,
        fixture,
        league: leagueOf(fixture),
        ai: null,
        community: [],
      });
    }
    const entry = fixtureMap.get(fixtureId);
    // Prefer the richest fixture object we have seen.
    if (fixture && (!entry.fixture || Object.keys(fixture).length > Object.keys(entry.fixture || {}).length)) {
      entry.fixture = fixture;
      entry.league = leagueOf(fixture);
    }
    return entry;
  };

  ai.forEach((item) => {
    const raw = item?.fixture || item?.fixtureData || {};
    // On AI predictions fixture.teams / fixture.league are raw ObjectIds; the
    // populated team + league objects (with names/logos) live at the top level.
    const hasNamedTeams =
      raw.teams && (raw.teams.home?.name || raw.teams.away?.name);
    const fixture = {
      ...raw,
      teams: hasNamedTeams
        ? raw.teams
        : { home: item.homeTeam || null, away: item.awayTeam || null },
      league: item.league || (raw.league?.name ? raw.league : {}),
      timestamp: raw.timestamp || item.timestamp || getAIFixtureTs(item),
    };
    const id = aiFixtureId(item);
    const entry = ensure(id, fixture);
    if (entry && !entry.ai) entry.ai = item;
  });

  community.forEach((item) => {
    const fixture = item?.fixture || {};
    const id = communityFixtureId(item);
    const entry = ensure(id, fixture);
    if (entry) entry.community.push(item);
  });

  const entries = Array.from(fixtureMap.values())
    .map((entry) => {
      const consensus = crowdConsensus(entry.community, userId);
      const lean = aiLean(entry.ai);
      const edge = aiCrowdEdge(lean, consensus);
      const decorated = {
        ...entry,
        consensus,
        aiReadout: lean,
        edge,
        ts: getAIFixtureTs(entry.ai) || getCommunityFixtureTs(entry.community[0]) || null,
        status: fixtureStatusTone(entry.fixture),
      };
      decorated.signal = signalStrength(decorated);
      return decorated;
    })
    .filter((entry) => isWithinTipsRetention(entry));

  // Group into leagues
  const leagueMap = new Map();
  entries.forEach((entry) => {
    const league = entry.league || {};
    const leagueId = String(league._id || league.id || league.name || "other");
    if (!leagueMap.has(leagueId)) {
      leagueMap.set(leagueId, {
        leagueId,
        leagueName: league.name || "Other",
        leagueLogo: league.logo || null,
        fixtures: [],
      });
    }
    leagueMap.get(leagueId).fixtures.push(entry);
  });

  const sections = Array.from(leagueMap.values()).map((section) => {
    const hasLive = section.fixtures.some((f) => f.status.tone === "live");
    const hasOpen = section.fixtures.some((f) => f.status.tone !== "finished");
    const aiCount = section.fixtures.filter((f) => f.ai).length;
    const crowdCount = section.fixtures.reduce((n, f) => n + f.consensus.count, 0);
    return { ...section, hasLive, hasOpen, aiCount, crowdCount };
  });

  return sections.sort((a, b) => {
    const liveDiff = b.hasLive - a.hasLive;
    if (liveDiff !== 0) return liveDiff;
    const openDiff = Number(b.hasOpen) - Number(a.hasOpen);
    if (openDiff !== 0) return openDiff;
    return a.leagueName.localeCompare(b.leagueName);
  });
};

/** Flat list of all fixture entries from board sections (keyboard nav etc). */
export const flattenBoard = (sections = []) =>
  sections.flatMap((s) => s.fixtures);

export const sortFixtures = (fixtures, sortId) => {
  const list = [...fixtures];
  const kickoff = (f) => Number(f.ts || 0);
  const bySecondary = (a, b) => {
    switch (sortId) {
      case "kickoff":
        return kickoff(a) - kickoff(b);
      case "crowd":
        return b.consensus.count - a.consensus.count;
      case "agreement": {
        const av = a.edge === "agree" ? 1 : 0;
        const bv = b.edge === "agree" ? 1 : 0;
        if (av !== bv) return bv - av;
        return b.signal - a.signal;
      }
      case "signal":
      default:
        return b.signal - a.signal;
    }
  };
  // Live / upcoming always above closed, then the selected secondary sort.
  return list.sort((a, b) => {
    const statusDiff = statusSortRank(a) - statusSortRank(b);
    if (statusDiff !== 0) return statusDiff;
    return bySecondary(a, b);
  });
};

export const matchesTipsFilter = (entry, filterId, ctx = {}) => {
  const { userId, favoriteTeam, followingIds = [] } = ctx;
  switch (filterId) {
    case "ai":
      return Boolean(entry.ai);
    case "crowd":
      return entry.consensus.count > 0;
    case "following":
      return (entry.community || []).some((tip) =>
        isBetFromFollowedCreator({ user: tip.user }, followingIds)
      );
    case "favorites":
      return fixtureHasFavoriteTeam(entry.fixture, favoriteTeam);
    case "live":
      return entry.status.tone === "live";
    case "closed":
      return entry.status.tone === "finished";
    case "today":
      return sameDayTs(entry.ts, 0);
    case "tomorrow":
      return sameDayTs(entry.ts, 1);
    case "unlocked":
      return (
        (entry.ai && entry.ai.isUnlocked) ||
        visibleCrowd(entry.community, userId).some((p) => !p.isPremium || p.isUnlockedByUser)
      );
    case "all":
    default:
      return true;
  }
};

export const matchesTipsSearch = (entry, query) => {
  const q = String(query || "").trim().toLowerCase();
  if (q.length < 2) return true;
  const fixture = entry.fixture || {};
  const haystack = [
    fixture.teams?.home?.name,
    fixture.teams?.away?.name,
    entry.league?.name,
    ...(entry.community || []).map((p) => p.user?.name),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
};

export const extractTipsLeagues = (sections = []) =>
  sortLeaguesByPriority(
    sections.map((s) => ({
      id: s.leagueId,
      apiId: s.leagueApiId ?? s.apiId ?? null,
      name: s.leagueName,
      logo: s.leagueLogo,
      count: s.fixtures.length,
    }))
  );

export const countTips = (sections = []) => {
  let fixtures = 0;
  let ai = 0;
  let crowd = 0;
  sections.forEach((s) => {
    fixtures += s.fixtures.length;
    ai += s.aiCount;
    crowd += s.crowdCount;
  });
  return { fixtures, ai, crowd };
};
